/**
 * Parses latitude and longitude from various map URL formats
 * (e.g., https://www.google.com/maps?q=40.7128,-74.006 or /@40.7128,-74.006).
 */
export const parseCoordinatesFromUrl = (
  url: string | null | undefined,
): { lat: number; lng: number } | null => {
  if (!url || typeof url !== "string") return null;

  // 1. ?q=lat,lng or ?query=lat,lng
  const queryMatch = url.match(/(?:query|q)=([^&]+)/);
  if (queryMatch) {
    const decoded = decodeURIComponent(queryMatch[1]);
    const parts = decoded.split(",");
    if (parts.length >= 2) {
      const lat = parseFloat(parts[0]);
      const lng = parseFloat(parts[1]);
      if (!isNaN(lat) && !isNaN(lng)) {
        return { lat, lng };
      }
    }
  }

  // 2. /@lat,lng
  const pathMatch = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (pathMatch) {
    const lat = parseFloat(pathMatch[1]);
    const lng = parseFloat(pathMatch[2]);
    if (!isNaN(lat) && !isNaN(lng)) {
      return { lat, lng };
    }
  }

  // 3. Direct coordinates "lat, lng"
  const directMatch = url.match(/^(-?\d+\.\d+)\s*,\s*(-?\d+\.\d+)$/);
  if (directMatch) {
    const lat = parseFloat(directMatch[1]);
    const lng = parseFloat(directMatch[2]);
    if (!isNaN(lat) && !isNaN(lng)) {
      return { lat, lng };
    }
  }

  return null;
};

/**
 * Resolves a human-readable address from an address URL or coordinates.
 * Uses sessionStorage to cache results and avoid rate limits.
 */
export const getAddressFromUrl = async (
  url: string | null | undefined,
): Promise<string> => {
  if (!url || typeof url !== "string") return "";

  const coords = parseCoordinatesFromUrl(url);

  // If no coordinates, check if there's a text query in ?q=...
  if (!coords) {
    const queryMatch = url.match(/(?:query|q)=([^&]+)/);
    if (queryMatch) {
      const decoded = decodeURIComponent(queryMatch[1]).replace(/\+/g, " ").trim();
      // If it doesn't look like lat,lng, return it directly
      if (!/^-?\d+(\.\d+)?,\s*-?\d+(\.\d+)?$/.test(decoded)) {
        return decoded;
      }
    }

    // If it's a plain address string (not a URL)
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      return url.trim();
    }
    return "";
  }

  const cacheKey = `geo_addr_${coords.lat.toFixed(5)}_${coords.lng.toFixed(5)}`;
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) return cached;
  } catch {}

  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${coords.lat}&lon=${coords.lng}&format=json&addressdetails=1`,
      {
        headers: {
          "Accept-Language": "en",
        },
      },
    );

    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();

    if (data?.display_name) {
      const address = data.display_name;
      try {
        sessionStorage.setItem(cacheKey, address);
      } catch {}
      return address;
    }
  } catch (err) {
    console.warn("Failed to reverse-geocode address from URL:", err);
  }

  return `${coords.lat.toFixed(4)}, ${coords.lng.toFixed(4)}`;
};
